import { useState } from 'react';
import { X, Loader2, CheckCircle, Calendar, Clock } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const BookingModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    property_type: '',
    address: '',
    city: '',
    booking_date: '',
    booking_time: '',
    special_instructions: ''
  });

  const services = [
    { id: 'kitchen-clean', label: 'Commercial Kitchen Clean' },
    { id: 'window', label: 'Window Cleaning' },
    { id: 'pressure-wash', label: 'Commercial High Pressure Wash' },
    { id: 'carpet', label: 'Carpet Cleaning' },
    { id: 'exhausts', label: 'Kitchen Exhausts' },
    { id: 'strip-seal', label: 'Strip and Seal' },
    { id: 'pizza-oven', label: 'Commercial Pizza Oven Clean' },
    { id: 'office-clean', label: 'Commercial Office Clean' }
  ];

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking. Please try again.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      service_type: '',
      property_type: '',
      address: '',
      city: '',
      booking_date: '',
      booking_time: '',
      special_instructions: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-80"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fadeInUp">
        {/* Header */}
        <div className="sticky top-0 bg-[#273268] text-white p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-bold text-2xl">Book a Service</h2>
            <p className="text-gray-600 text-sm mt-1">Schedule your cleaning appointment</p>
          </div>
          <button 
            onClick={handleClose}
            className="hover:text-[#3B82F6] transition-colors"
            data-testid="booking-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12 animate-fadeIn" data-testid="booking-success">
              <div className="w-20 h-20 bg-[#10B981] mx-auto mb-6 flex items-center justify-center">
                <CheckCircle size={40} className="text-[#0A0A0A]" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4">Booking Confirmed!</h3>
              <p className="text-gray-500 mb-2">Your cleaning service has been scheduled for:</p>
              <p className="font-mono text-lg font-bold mb-8">
                {formData.booking_date} at {formData.booking_time}
              </p>
              <p className="text-gray-500 mb-8 text-sm">
                A confirmation email has been sent to {formData.email}
              </p>
              <button onClick={handleClose} className="btn-primary">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 animate-fadeIn">
              {/* Service Selection */}
              <div>
                <label className="form-label">Service Type *</label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                  className="form-input"
                  required
                  data-testid="booking-service-select"
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>{service.label}</option>
                  ))}
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label className="form-label">Property Type *</label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                  className="form-input"
                  required
                  data-testid="booking-property-select"
                >
                  <option value="">Select property type</option>
                  <option value="office">Office</option>
                  <option value="retail">Retail Store</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="medical">Medical Facility</option>
                  <option value="residential">House/Apartment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Calendar size={14} />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.booking_date}
                    onChange={(e) => setFormData({...formData, booking_date: e.target.value})}
                    className="form-input"
                    required
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="booking-date-input"
                  />
                </div>
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Clock size={14} />
                    Time *
                  </label>
                  <select
                    value={formData.booking_time}
                    onChange={(e) => setFormData({...formData, booking_time: e.target.value})}
                    className="form-input"
                    required
                    data-testid="booking-time-select"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="form-label">Address *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="form-input"
                  placeholder="Street address"
                  required
                  data-testid="booking-address-input"
                />
              </div>

              {/* City */}
              <div>
                <label className="form-label">City *</label>
                <select
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="form-input"
                  required
                  data-testid="booking-city-select"
                >
                  <option value="">Select city</option>
                  <option value="Melbourne">Melbourne</option>
                  <option value="Sydney">Sydney</option>
                  <option value="Gold Coast">Gold Coast</option>
                </select>
              </div>

              {/* Contact Details */}
              <div className="pt-4 border-t border-gray-300">
                <h3 className="font-heading font-bold mb-4">Contact Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="form-input"
                      placeholder="Your full name"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="form-input"
                        placeholder="your@email.com"
                        required
                        data-testid="booking-email-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone *</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="form-input"
                        placeholder="0400 000 000"
                        required
                        data-testid="booking-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              <div>
                <label className="form-label">Special Instructions (Optional)</label>
                <textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                  className="form-input resize-none"
                  rows={3}
                  placeholder="Any special requirements or access instructions..."
                  data-testid="booking-instructions-input"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="booking-submit-btn"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  'Confirm Booking'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
